import { useEffect, useMemo, useRef, useState } from "react";
import {
  changeEmployeePassword,
  getEmployeeSettings,
  updateEmployeeSettings,
} from "../api/instateamSettingsApi";
import {
  getNotificationSettings,
  updateNotificationSettings,
} from "../api/instateamNotificationsApi";
import { AppIcon } from "../components/InstateamEmployeeComponents";
import {
  applyEmployeeTheme,
  formatDateValue,
  formatFieldValue,
  getPresenceStatusMeta,
  normalizeHexColor,
  resolveEmployeeAccentColor,
  resolveEmployeeProfileStatus,
} from "../utils/employeeHelpers";

const DEFAULT_SETTINGS = {
  theme: "warm",
  customAccentColor: "#ffc21a",
  presenceStatus: "online",
};

const DEFAULT_NOTIFICATION_SETTINGS = {
  channels: { inApp: true },
  preferences: {
    complaints: true,
    leave: true,
    tasks: true,
    announcements: true,
    payroll: true,
    attendance: true,
    profile: true,
    system: true,
  },
  quietHours: {
    enabled: true,
    startTime: "22:00",
    endTime: "07:00",
  },
};

const INITIAL_PASSWORD_FORM = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

const THEME_OPTIONS = [
  {
    key: "warm",
    label: "Warm Yellow",
    hint: "Default Insta Team accent",
    color: "#ffc21a",
  },
  {
    key: "emerald",
    label: "Emerald Green",
    hint: "Fresh and calm accent",
    color: "#22c55e",
  },
  {
    key: "ocean",
    label: "Ocean Blue",
    hint: "Clean and focused accent",
    color: "#3b82f6",
  },
  {
    key: "custom",
    label: "Custom",
    hint: "Use your own accent color",
    color: "#f97316",
  },
];

const STATUS_OPTIONS = [
  {
    key: "online",
    label: "Online",
    description: "Green check badge shown on your avatar.",
  },
  {
    key: "right_back",
    label: "Right back",
    description: "Yellow clock badge for a short away status.",
  },
  {
    key: "busy",
    label: "Busy",
    description: "Red dot badge while you are focused.",
  },
  {
    key: "out_of_office",
    label: "Out of office",
    description: "Logout badge when you are unavailable.",
  },
];

const NOTIFICATION_OPTIONS = [
  ["tasks", "Tasks", "Get notified when tasks are assigned or updated.", "clipboard"],
  ["leave", "Leave Requests", "Receive leave approval and rejection updates.", "checkCircle"],
  ["attendance", "Attendance", "Attendance reminders and related alerts.", "calendar"],
  ["announcements", "Announcements", "Company-wide updates and notices.", "megaphone"],
  ["payroll", "Payroll", "Payslip, salary, and payroll update alerts.", "wallet"],
  ["complaints", "Complaints", "Complaint updates and admin responses.", "chat"],
  ["profile", "Profile & Account", "Profile and account change notifications.", "user"],
  ["system", "System", "Important portal notifications.", "settings"],
];

function buildAccountPayload(source = {}) {
  return {
    fullName: source.fullName || "",
    email: source.email || "",
    username: source.username || "",
    employeeId: source.employeeId || "",
    department: source.department || "",
    designation: source.designation || "",
    lastLoginAt: source.lastLoginAt || null,
  };
}

function buildNotificationState(payload = {}) {
  return {
    channels: {
      inApp: true,
      ...(payload.channels || {}),
    },
    preferences: {
      ...DEFAULT_NOTIFICATION_SETTINGS.preferences,
      ...(payload.preferences || {}),
    },
    quietHours: {
      ...DEFAULT_NOTIFICATION_SETTINGS.quietHours,
      ...(payload.quietHours || {}),
    },
  };
}

function SettingSwitch({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      className={`employee-settings-switch ${checked ? "active" : ""}`}
      onClick={() => !disabled && onChange?.(!checked)}
      disabled={disabled}
    >
      <span />
    </button>
  );
}

export default function EmployeeSettingsView({ employee, token, onEmployeeUpdate }) {
  const [settings, setSettings] = useState({
    ...DEFAULT_SETTINGS,
    presenceStatus: resolveEmployeeProfileStatus(employee),
  });
  const [savedSettings, setSavedSettings] = useState(settings);
  const [account, setAccount] = useState(buildAccountPayload(employee));
  const [notificationSettings, setNotificationSettings] = useState(
    DEFAULT_NOTIFICATION_SETTINGS
  );
  const [savedNotificationSettings, setSavedNotificationSettings] = useState(
    DEFAULT_NOTIFICATION_SETTINGS
  );
  const [passwordForm, setPasswordForm] = useState(INITIAL_PASSWORD_FORM);
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const persistedSettingsRef = useRef(savedSettings);

  useEffect(() => {
    persistedSettingsRef.current = savedSettings;
  }, [savedSettings]);

  useEffect(() => {
    if (!token) return undefined;
    let isActive = true;

    async function loadPageData() {
      try {
        setIsLoading(true);
        const [settingsPayload, notificationsPayload] = await Promise.all([
          getEmployeeSettings(token),
          getNotificationSettings(token),
        ]);

        if (!isActive) return;

        const nextSettings = {
          ...DEFAULT_SETTINGS,
          ...(settingsPayload.settings || {}),
        };

        if (!normalizeHexColor(nextSettings.customAccentColor)) {
          nextSettings.customAccentColor = DEFAULT_SETTINGS.customAccentColor;
        }

        const nextAccount = buildAccountPayload({
          ...employee,
          ...(settingsPayload.account || {}),
        });
        const nextNotificationSettings = buildNotificationState(
          notificationsPayload.preferences
        );

        setSettings(nextSettings);
        setSavedSettings(nextSettings);
        setAccount(nextAccount);
        setNotificationSettings(nextNotificationSettings);
        setSavedNotificationSettings(nextNotificationSettings);
      } catch (error) {
        if (isActive) {
          setMessage({ type: "error", text: error.message });
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }

    loadPageData();

    return () => {
      isActive = false;
    };
  }, [employee, token]);

  useEffect(() => {
    applyEmployeeTheme(settings);
  }, [settings]);

  useEffect(
    () => () => {
      applyEmployeeTheme(persistedSettingsRef.current || DEFAULT_SETTINGS);
    },
    []
  );

  const hasPreferenceChanges = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(savedSettings),
    [savedSettings, settings]
  );

  const hasNotificationChanges = useMemo(
    () =>
      JSON.stringify(notificationSettings) !==
      JSON.stringify(savedNotificationSettings),
    [notificationSettings, savedNotificationSettings]
  );

  const previewAccent = resolveEmployeeAccentColor(settings);
  const activePresence = getPresenceStatusMeta(settings.presenceStatus);

  function updateSetting(key, value) {
    setSettings((current) => ({ ...current, [key]: value }));
  }

  function updateNotificationPreference(key, value) {
    setNotificationSettings((current) => ({
      ...current,
      preferences: {
        ...current.preferences,
        [key]: value,
      },
    }));
  }

  async function savePreferences() {
    try {
      setIsSavingPreferences(true);
      setMessage(null);

      const payload = await updateEmployeeSettings(token, {
        ...settings,
        customAccentColor:
          normalizeHexColor(settings.customAccentColor) ||
          DEFAULT_SETTINGS.customAccentColor,
      });

      const nextSettings = {
        ...DEFAULT_SETTINGS,
        ...(payload.settings || settings),
      };

      setSettings(nextSettings);
      setSavedSettings(nextSettings);
      setAccount(buildAccountPayload(payload.employee || employee));
      onEmployeeUpdate?.(payload.employee);
      setMessage({
        type: "success",
        text: "Theme and status updated successfully.",
      });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setIsSavingPreferences(false);
    }
  }

  async function saveNotifications() {
    try {
      setIsSavingNotifications(true);
      setMessage(null);

      const payload = await updateNotificationSettings(token, notificationSettings);
      const nextNotificationSettings = buildNotificationState(payload.preferences);

      setNotificationSettings(nextNotificationSettings);
      setSavedNotificationSettings(nextNotificationSettings);
      setMessage({
        type: "success",
        text: "Notification settings updated successfully.",
      });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setIsSavingNotifications(false);
    }
  }

  async function handlePasswordSubmit(event) {
    event.preventDefault();

    try {
      setIsChangingPassword(true);
      setMessage(null);
      await changeEmployeePassword(token, passwordForm);
      setPasswordForm(INITIAL_PASSWORD_FORM);
      setMessage({
        type: "success",
        text: "Password changed successfully.",
      });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setIsChangingPassword(false);
    }
  }

  return (
    <section className="employee-settings-page slim">
      <div className="employee-settings-hero compact">
        <div>
          <span className="employee-settings-kicker">Portal Preferences</span>
          <h1>Settings</h1>
          <p>
            Manage your accent theme, profile status, notifications, and account
            access from one compact page.
          </p>
        </div>

        <div className="employee-settings-hero-card compact">
          <div
            className="employee-settings-preview-chip"
            style={{ "--settings-preview-accent": previewAccent }}
          >
            <span />
          </div>
          <div>
            <strong>{formatFieldValue(account.fullName)}</strong>
            <small>{activePresence.label}</small>
          </div>
        </div>
      </div>

      {message ? (
        <div className={`employee-settings-message ${message.type}`}>
          <AppIcon name="infoCircle" />
          <span>{message.text}</span>
        </div>
      ) : null}

      {isLoading ? (
        <div className="employee-settings-loading">Loading settings...</div>
      ) : null}

      <div className="employee-settings-layout compact">
        <div className="employee-settings-main">
          <article className="employee-settings-panel">
            <div className="employee-settings-panel-title">
              <span>
                <AppIcon name="palette" />
              </span>
              <div>
                <h2>Theme</h2>
                <p>
                  Choose which accent replaces the yellow highlight across your
                  employee portal.
                </p>
              </div>
            </div>

            <div className="employee-theme-grid">
              {THEME_OPTIONS.map((theme) => {
                const isActive = settings.theme === theme.key;
                const swatchColor =
                  theme.key === "custom" ? settings.customAccentColor : theme.color;

                return (
                  <button
                    key={theme.key}
                    type="button"
                    className={`employee-theme-card ${isActive ? "active" : ""}`}
                    onClick={() => updateSetting("theme", theme.key)}
                  >
                    <span
                      className="employee-theme-swatch"
                      style={{ "--settings-preview-accent": swatchColor }}
                    />
                    <div>
                      <strong>{theme.label}</strong>
                      <small>{theme.hint}</small>
                    </div>
                    {isActive ? <AppIcon name="check" /> : null}
                  </button>
                );
              })}
            </div>

            {settings.theme === "custom" ? (
              <div className="employee-theme-custom-row">
                <label className="employee-theme-picker-card">
                  <span>Custom Accent Color</span>
                  <div className="employee-theme-picker-field">
                    <input
                      type="color"
                      value={
                        normalizeHexColor(settings.customAccentColor) ||
                        DEFAULT_SETTINGS.customAccentColor
                      }
                      onChange={(event) =>
                        updateSetting("customAccentColor", event.target.value)
                      }
                    />
                    <strong>
                      {normalizeHexColor(settings.customAccentColor) ||
                        DEFAULT_SETTINGS.customAccentColor}
                    </strong>
                  </div>
                </label>
              </div>
            ) : null}
          </article>

          <article className="employee-settings-panel">
            <div className="employee-settings-panel-title">
              <span>
                <AppIcon name="users" />
              </span>
              <div>
                <h2>Status</h2>
                <p>
                  This badge appears with your photo in chat, tasks, and other
                  employee lists.
                </p>
              </div>
            </div>

            <div className="employee-presence-grid">
              {STATUS_OPTIONS.map((statusOption) => {
                const meta = getPresenceStatusMeta(statusOption.key);
                const isActive = settings.presenceStatus === statusOption.key;

                return (
                  <button
                    key={statusOption.key}
                    type="button"
                    className={`employee-presence-card ${isActive ? "active" : ""}`}
                    onClick={() => updateSetting("presenceStatus", statusOption.key)}
                  >
                    <span className={`employee-presence-card-icon ${meta.className}`}>
                      {meta.icon ? (
                        <AppIcon name={meta.icon} />
                      ) : (
                        <span className="employee-presence-dot" />
                      )}
                    </span>
                    <div>
                      <strong>{statusOption.label}</strong>
                      <small>{statusOption.description}</small>
                    </div>
                    {isActive ? <AppIcon name="check" /> : null}
                  </button>
                );
              })}
            </div>

            <div className="employee-settings-actions aligned-right">
              <button
                className="secondary-button"
                type="button"
                onClick={() => setSettings(savedSettings)}
                disabled={!hasPreferenceChanges || isSavingPreferences}
              >
                Reset
              </button>
              <button
                className="primary-button"
                type="button"
                onClick={savePreferences}
                disabled={!hasPreferenceChanges || isSavingPreferences}
              >
                <AppIcon name="check" />
                {isSavingPreferences ? "Saving..." : "Save Theme & Status"}
              </button>
            </div>
          </article>

          <article className="employee-settings-panel">
            <div className="employee-settings-panel-title">
              <span>
                <AppIcon name="bell" />
              </span>
              <div>
                <h2>Notifications</h2>
                <p>
                  Choose which updates should reach you and when quiet hours should
                  be active.
                </p>
              </div>
            </div>

            <div className="employee-settings-notification-list">
              {NOTIFICATION_OPTIONS.map(([key, label, description, icon]) => (
                <div key={key} className="employee-settings-notification-row">
                  <span className="employee-settings-small-icon">
                    <AppIcon name={icon} />
                  </span>
                  <div>
                    <strong>{label}</strong>
                    <p>{description}</p>
                  </div>
                  <SettingSwitch
                    checked={notificationSettings.preferences?.[key] !== false}
                    onChange={(checked) => updateNotificationPreference(key, checked)}
                  />
                </div>
              ))}
            </div>

            <div className="employee-settings-quiet-card">
              <div className="employee-settings-toggle-card">
                <div>
                  <span className="employee-settings-small-icon">
                    <AppIcon name="clock" />
                  </span>
                  <div>
                    <strong>Quiet Hours</strong>
                    <p>Pause non-urgent alerts during your focus time.</p>
                  </div>
                </div>
                <SettingSwitch
                  checked={Boolean(notificationSettings.quietHours?.enabled)}
                  onChange={(checked) =>
                    setNotificationSettings((current) => ({
                      ...current,
                      quietHours: {
                        ...current.quietHours,
                        enabled: checked,
                      },
                    }))
                  }
                />
              </div>

              <div className="employee-settings-time-grid">
                <label>
                  Start Time
                  <input
                    type="time"
                    value={notificationSettings.quietHours?.startTime || "22:00"}
                    onChange={(event) =>
                      setNotificationSettings((current) => ({
                        ...current,
                        quietHours: {
                          ...current.quietHours,
                          startTime: event.target.value,
                        },
                      }))
                    }
                  />
                </label>
                <label>
                  End Time
                  <input
                    type="time"
                    value={notificationSettings.quietHours?.endTime || "07:00"}
                    onChange={(event) =>
                      setNotificationSettings((current) => ({
                        ...current,
                        quietHours: {
                          ...current.quietHours,
                          endTime: event.target.value,
                        },
                      }))
                    }
                  />
                </label>
              </div>
            </div>

            <div className="employee-settings-actions aligned-right">
              <button
                className="secondary-button"
                type="button"
                onClick={() => setNotificationSettings(savedNotificationSettings)}
                disabled={!hasNotificationChanges || isSavingNotifications}
              >
                Reset
              </button>
              <button
                className="primary-button"
                type="button"
                onClick={saveNotifications}
                disabled={!hasNotificationChanges || isSavingNotifications}
              >
                <AppIcon name="check" />
                {isSavingNotifications ? "Saving..." : "Save Notifications"}
              </button>
            </div>
          </article>
        </div>

        <aside className="employee-settings-side">
          <article className="employee-settings-panel">
            <div className="employee-settings-panel-title">
              <span>
                <AppIcon name="userCircle" />
              </span>
              <div>
                <h2>Profile Info</h2>
                <p>Quick account details currently linked to your employee login.</p>
              </div>
            </div>

            <div className="employee-settings-info-row">
              <span>Full Name</span>
              <strong>{formatFieldValue(account.fullName)}</strong>
            </div>
            <div className="employee-settings-info-row">
              <span>Username</span>
              <strong>{formatFieldValue(account.username)}</strong>
            </div>
            <div className="employee-settings-info-row">
              <span>Email</span>
              <strong>{formatFieldValue(account.email)}</strong>
            </div>
            <div className="employee-settings-info-row">
              <span>Employee ID</span>
              <strong>{formatFieldValue(account.employeeId)}</strong>
            </div>
            <div className="employee-settings-info-row">
              <span>Department</span>
              <strong>{formatFieldValue(account.department)}</strong>
            </div>
            <div className="employee-settings-info-row">
              <span>Designation</span>
              <strong>{formatFieldValue(account.designation)}</strong>
            </div>
            <div className="employee-settings-info-row">
              <span>Last Login</span>
              <strong>{formatDateValue(account.lastLoginAt)}</strong>
            </div>
          </article>

          <article className="employee-settings-panel">
            <div className="employee-settings-panel-title">
              <span>
                <AppIcon name="lock" />
              </span>
              <div>
                <h2>Change Password</h2>
                <p>Update your employee panel login password securely.</p>
              </div>
            </div>

            <form
              className="employee-settings-password-form"
              onSubmit={handlePasswordSubmit}
            >
              <label className="employee-settings-password-field">
                <span>Current Password</span>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(event) =>
                    setPasswordForm((current) => ({
                      ...current,
                      currentPassword: event.target.value,
                    }))
                  }
                  placeholder="Enter current password"
                />
              </label>

              <label className="employee-settings-password-field">
                <span>New Password</span>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(event) =>
                    setPasswordForm((current) => ({
                      ...current,
                      newPassword: event.target.value,
                    }))
                  }
                  placeholder="Enter new password"
                />
              </label>

              <label className="employee-settings-password-field">
                <span>Confirm Password</span>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(event) =>
                    setPasswordForm((current) => ({
                      ...current,
                      confirmPassword: event.target.value,
                    }))
                  }
                  placeholder="Confirm new password"
                />
              </label>

              <button
                className="primary-button"
                type="submit"
                disabled={isChangingPassword}
              >
                <AppIcon name="lock" />
                {isChangingPassword ? "Updating..." : "Change Password"}
              </button>
            </form>
          </article>
        </aside>
      </div>
    </section>
  );
}
